import React from 'react';
import { FieldValues } from 'react-hook-form';
import FadeTransition from '../../animations/transitions/fade-transition';
import FormProvider from '../../primitives/form-provider';
import FormRegion from '../../primitives/form-region';
import FormFieldset from '../../primitives/form-fieldset';
import Input from '../../primitives/input';
import StyledButton from '../../primitives/styled-button';
import FormSuccess from '../../primitives/form-success';
import Select, { SelectOption } from '../../primitives/select';
import useGeoByAddress from '../../../hooks/use-geo-by-address';
import TextArea from '../../primitives/text-area';
import { canadianProvincesAndTerritories, usStatesAndTerritories } from '../../../constants/states';
import Divider from '../../primitives/divider';
import RowSet from '../../primitives/grid/row-set';

const usAndCanStates = usStatesAndTerritories.concat(canadianProvincesAndTerritories);

interface PreferredVendorFormProps {
    submitButton?: string
    successMessage?: string
}

const selectPlaceholder = 'Choose...';

const serviceOptions = [
    'Answering Phone Services',
    'Asset Tracking',
    'Auto Glass Repair',
    'Auto Painting',
    'Awards & Employee Recognition',
    'Background Screening and Drug Testing',
    'Boxes and Packaging Supplies',
    'Car Rentals',
    'Cleaning Supplies',
    'Computers and Technology',
    'Construction Software',
    'Contents Listing Software',
    'Credit Card Processing',
    'Credit Cards',
    'Custom Shelving',
    'Debt Collections',
    'Digital Marketing',
    'Dry Ice Blasting Equipment',
    'Dumpster Rental',
    'Electrical (Suppliers and Subcontractors)',
    'Emergency Board Up',
    'Environmental Contracting/Hygienist/Safety Consulting',
    'Environmental Testing',
    'Equipment',
    'Equipment Rental',
    'Financial/Accounting Consultation',
    'Fleet Purchasing Card',
    'Fleet Safety Solutions',
    'Flooring (Suppliers and Subcontractors)',
    'Flooring Analysis and Testing',
    'Fuel Delivery',
    'General Contracting and Consulting',
    'GPS Tracking',
    'Hotels/Lodging',
    'HVAC (Suppliers and Subcontractors)',
    'Insurance (Life or Health)',
    'Job Search',
    'Legal Services',
    'Managed Print Services',
    'Maps',
    'Meal Food Packages',
    'Mobile Offices',
    'Moving Companies',
    'Office Resources',
    'Operating Supplies',
    'Other',
    'Payroll/Human Resources',
    'Personality/Sales Assessments',
    'Portable Containers',
    'Portable Restrooms and Kitchens',
    'Portable Shelters',
    'Printing Services (Flyers, Business Cards, Stationary)',
    'Promotional/Marketing Items',
    'Recruitment Personnel Services',
    'Restoration Services (Document, Art, and Electronics)',
    'Roofing',
    'Safety Products',
    'Sales Leads',
    'Security Services',
    'Signage',
    'Tarps',
    'Temporary Housing',
    'Temporary Labor',
    'Tents (Promotional)',
    'Textile Dry Cleaning',
    'Time and Attendance',
    'Tires',
    'Towels',
    'Trade Show Services',
    'Training',
    'Transcription Services',
    'Tree Removal',
    'Truck Rental Services',
    'Uniforms and Branded Apparel',
    'Vehicle Maintenance',
    'Vehicle Wraps',
    'Vendor Management',
    'Voice and Data Services',
    'Waste Management Services (Debris Removal and Hazardous Materials)',
    'Water Treatment Technology',
    'Wireless Services',
];

const revenueOptions = [
    '$1 - $100,000',
    '$100,001 - $500,000',
    '$500,001 - $1,000,000',
    '$1,000,001+',
];

const NoServicesProvidedMessage: React.FC = () => (
    <div css={{
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '24px',
        margin: '16px auto',
        maxWidth: '600px',
        textAlign: 'center',
    }}
    >
        <h3 css={{
            margin: '0 0 16px 0',
            color: '#495057',
            fontSize: '18px',
            fontWeight: '600',
        }}
        >
            Application Requirements Not Met
        </h3>
        <p css={{
            margin: '0 0 16px 0',
            color: '#6c757d',
            lineHeight: '1.6',
            fontSize: '14px',
        }}
        >
            Thank you for your interest in becoming a SERVPRO preferred vendor. Currently, we require current sales with SERVPRO franchises as part of our criteria. As this requirement hasn&apos;t been met, we&apos;re unable to move forward with your application.
        </p>
        <p css={{
            margin: '0',
            color: '#6c757d',
            lineHeight: '1.6',
            fontSize: '14px',
        }}
        >
            We encourage you to build relationships with our SERVPRO franchises and reapply once this criterion is fulfilled. We appreciate your interest and look forward to the possibility of working together at a future date.
        </p>
    </div>
);

const PreferredVendorForm = ({ submitButton, successMessage }: PreferredVendorFormProps): JSX.Element => {
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const [currentlyProvidingServicesIsDirty, setCurrentlyProvidingServicesIsDirty] = React.useState<boolean | null>(null);
    const [currentProvidedServiceIsOther, setCurrentProvidedServiceIsOther] = React.useState<boolean | null>(null);
    const { data, fetchGeo } = useGeoByAddress();

    const successMessageRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (showSuccess && successMessageRef?.current) {
            setTimeout(() => {
                if (successMessageRef?.current) {
                    successMessageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 500);
        }
    }, [showSuccess]);

    const onSubmit = (e: FieldValues): void => {
        setIsLoading(true);
        fetch('/api/forms/vendor-lead', {
            method: 'POST',
            body: JSON.stringify(e),
            headers: {
                'Content-Type': 'application/json',
                'cache-control': 'no-cache',
            },
        }).then((res) => {
            setIsLoading(false);
            return res.json();
        }).then(() => {
            setShowSuccess(true);
            return true;
        }).catch(() => {
            setIsLoading(false);
            setShowSuccess(false);
            return false;
        });
    };

    return (
        <FadeTransition shouldChange={showSuccess.toString()}>
            {!showSuccess && (
                <FormProvider
                    onSubmit={onSubmit}
                    disabled={isLoading || showSuccess}
                    values={{
                        city: data?.city,
                        state: data?.stateShort,
                    }}
                >
                    <RowSet breakpoints={{
                        dt: { between: 32 },
                        mb: { between: 16 },
                    }}
                    >
                        <FormRegion description="Company Information">
                            <FormFieldset alignLeft name="">
                                <span>
                                    <Input name="company" maxLength={40} placeholder="Company Name" required />
                                </span>
                                <span>
                                    <Input
                                        onChange={(e) => {
                                            if (e?.currentTarget?.value && e.currentTarget.value.length === 5) {
                                                fetchGeo(e.currentTarget.value);
                                            }
                                        }}
                                        minLength={5}
                                        maxLength={20}
                                        required
                                        name="zip"
                                        placeholder="Zip"
                                    />
                                </span>
                            </FormFieldset>
                            {data && data?.city && (
                                <FormFieldset alignLeft name="">
                                    <>
                                        <div css={{ width: '100%' }}>
                                            <Input name="street" placeholder="Company HQ Street Address" required />
                                        </div>
                                        <span>
                                            <Input required name="city" placeholder="City" />
                                        </span>
                                        <span>
                                            <Select required name="state" placeholder="State/Province">
                                                {usAndCanStates.map(item => (
                                                    <SelectOption key={`state-${item.abbreviation}`} value={item.abbreviation}>
                                                        {item.name}
                                                    </SelectOption>
                                                ))}
                                            </Select>
                                        </span>
                                    </>
                                </FormFieldset>
                            )}
                            <FormFieldset alignLeft name="">
                                <Input name="url" maxLength={80} placeholder="Website" required />
                            </FormFieldset>
                        </FormRegion>
                        <Divider />
                        <FormRegion description="Contact Information">
                            <FormFieldset alignLeft name="">
                                <span>
                                    <Input name="first-name" placeholder="Your First Name" required />
                                </span>
                                <span>
                                    <Input name="last-name" placeholder="Your Last Name" required />
                                </span>
                                <span>
                                    <Input name="title" placeholder="Title" />
                                </span>
                                <span>
                                    <Input name="phone" type="tel" placeholder="Phone Number" autoComplete="tel" />
                                </span>
                                <span>
                                    <Input name="email" placeholder="Email Address" autoComplete="email" required />
                                </span>
                            </FormFieldset>
                        </FormRegion>
                        <Divider />
                        <FormRegion description="Product/Service Details">
                            <FormFieldset alignLeft name="Are you currently providing products / services to SERVPRO franchises?">
                                <div css={{ width: '100%' }}>
                                    <Select
                                        required
                                        name="currently-providing-services"
                                        placeholder={selectPlaceholder}
                                        onChange={(e) => {
                                            setCurrentlyProvidingServicesIsDirty(e === 'yes');
                                        }}
                                    >
                                        <SelectOption value="yes">Yes</SelectOption>
                                        <SelectOption value="no">No</SelectOption>
                                    </Select>
                                </div>
                            </FormFieldset>
                            {currentlyProvidingServicesIsDirty === true && (
                                <>
                                    <FormFieldset alignLeft name="How much revenue are you currently doing with SERVPRO annually?">
                                        <div css={{ width: '100%' }}>
                                            <Select
                                                required
                                                placeholder={selectPlaceholder}
                                                name='current-annual-servpro-revenue'
                                            >
                                                {revenueOptions.map(item => (
                                                    <SelectOption key={item} value={item}>
                                                        {item}
                                                    </SelectOption>
                                                ))}
                                            </Select>
                                        </div>
                                    </FormFieldset>
                                    {/* eslint-disable-next-line max-len */}
                                    <FormFieldset alignLeft name='As part of our vendor selection process, we require all vendors to agree to a referral revenue arrangement. Specifically, this involves paying a percentage of sales generated. Only vendors who agree to this arrangement will be considered for partnership. Do you agree?'>
                                        <div css={{ width: '100%' }}>
                                            <Select
                                                required
                                                placeholder={selectPlaceholder}
                                                name='agreed-to-referral'
                                            >
                                                <SelectOption value="no">No</SelectOption>
                                                <SelectOption value="yes">Yes</SelectOption>
                                            </Select>
                                        </div>
                                    </FormFieldset>
                                    <FormFieldset alignLeft name="We require all participating vendors to offer discounted pricing. Please confirm if you are able to provide exclusive discounted pricing to our franchises.">
                                        <div css={{ width: '100%' }}>
                                            <Select
                                                required
                                                placeholder={selectPlaceholder}
                                                name='agreed-to-discounted-pricing'
                                            >
                                                <SelectOption value="no">No</SelectOption>
                                                <SelectOption value="yes">Yes</SelectOption>
                                            </Select>
                                        </div>
                                    </FormFieldset>
                                    <FormFieldset alignLeft name="Product/Services Provided">
                                        <div css={{ width: '100%' }}>
                                            <Select
                                                required
                                                name="service-provided"
                                                placeholder={selectPlaceholder}
                                                onChange={(e) => {
                                                    setCurrentProvidedServiceIsOther(e === 'Other');
                                                }}
                                            >
                                                {serviceOptions.map(item => (
                                                    <SelectOption key={item} value={item}>
                                                        {item}
                                                    </SelectOption>
                                                ))}
                                            </Select>
                                        </div>
                                    </FormFieldset>
                                    {currentProvidedServiceIsOther && (
                                        <FormFieldset alignLeft name="Product/Services Other Notes:">
                                            <TextArea
                                                name="other-notes"
                                                placeholder='Other Notes'
                                            />
                                        </FormFieldset>
                                    )}
                                    <FormFieldset alignLeft name="Products/Services Additional Notes:">
                                        <TextArea
                                            name="service-notes"
                                            placeholder="Additional Notes"
                                        />
                                    </FormFieldset>
                                    <Divider />
                                    <FormRegion description="">
                                        <FormFieldset alignLeft name="">
                                            <StyledButton type="submit" loading={isLoading}>
                                                {submitButton || 'Submit'}
                                            </StyledButton>
                                        </FormFieldset>
                                    </FormRegion>
                                </>
                            )}
                            {currentlyProvidingServicesIsDirty === false && (
                                <NoServicesProvidedMessage />
                            )}

                        </FormRegion>
                    </RowSet>
                </FormProvider>
            )}
            <div ref={successMessageRef}>
                {showSuccess && (
                    <FormSuccess dangerouslySetInnerHTML={{ __html: successMessage || 'Submitted successfully.' }} />
                )}
            </div>
        </FadeTransition>
    );
};

export default PreferredVendorForm;
